import { mutate } from 'swr';
import { useMemo, useCallback } from 'react';

import { useAuthenticatedAxios } from 'src/hooks/useAuthenticatedAxios';

import { authenticatedSwrOptions } from 'src/utils/constants';

import { zerreactorBackendEndpoints } from 'src/api/services/zerreactor_backend_definition/zerreactorBackendEndpoints';

import useAuthenticatedSWR from 'src/auth/hooks/use-authenticated-swr';

export function useUsersOperations() {
  const authenticatedAxios = useAuthenticatedAxios();

  // Updated to use Auth.signUpUser endpoint with simplified data structure
  const inviteUser = useCallback(
    async (userData) => {
      try {
        const response = await authenticatedAxios.post(
          zerreactorBackendEndpoints.Auth.signUpUser,
          userData
        );

        return response;
      } catch (error) {
        console.error('Error inviting user:', error);
        throw error;
      }
    },
    [authenticatedAxios]
  );

  // Updated to handle new API structure with all required fields
  const updateUser = useCallback(
    async (userId, userData) => {
      try {
        const response = await authenticatedAxios.put(
          zerreactorBackendEndpoints.User.Update(userId),
          userData
        );

        mutate(zerreactorBackendEndpoints.User.GetById(userId), (currentData) => ({
          ...currentData,
          ...response.data,
        }));

        return response;
      } catch (error) {
        console.error('Error updating user:', error);
        throw error;
      }
    },
    [authenticatedAxios]
  );

  const updateOnFirstLogin = useCallback(
    async (userId) => {
      try {
        const response = await authenticatedAxios.put(
          zerreactorBackendEndpoints.User.UpdateOnFirstLogin(userId)
        );

        return response;
      } catch (error) {
        console.error('Error updating user:', error);
        throw error;
      }
    },
    [authenticatedAxios]
  );

  const deleteUser = useCallback(
    async (userId) => {
      try {
        const response = await authenticatedAxios.delete(
          zerreactorBackendEndpoints.User.Delete(userId)
        );

        mutate(zerreactorBackendEndpoints.User.GetAll);

        return response;
      } catch (error) {
        console.error('Error deleting user:', error);
        throw error;
      }
    },
    [authenticatedAxios]
  );

  const sendVerificationEmail = useCallback(
    async (userId) => {
      try {
        const response = await authenticatedAxios.post(
          zerreactorBackendEndpoints.User.SendVerificationMail(userId)
        );

        return response;
      } catch (error) {
        console.error('Error sending verification email:', error);
        throw error;
      }
    },
    [authenticatedAxios]
  );

  const sendResetPasswordEmail = useCallback(
    async (userId) => {
      try {
        const response = await authenticatedAxios.post(
          zerreactorBackendEndpoints.User.SendResetPasswordEmail(userId)
        );

        return response;
      } catch (error) {
        console.error('Error sending reset password email:', error);
        throw error;
      }
    },
    [authenticatedAxios]
  );

  return {
    inviteUser,
    updateOnFirstLogin,
    deleteUser,
    updateUser,
    sendVerificationEmail,
    sendResetPasswordEmail,
  };
}

export function useGetStudents(organizationId) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    organizationId ? zerreactorBackendEndpoints.User.GetStudents(organizationId) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      students: data || [],
      studentsLoading: isLoading,
      studentsError: error,
      studentsValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetTeachers(organizationId) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    organizationId ? zerreactorBackendEndpoints.User.GetTeachers(organizationId) : null,
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      teachers: data || [],
      teachersLoading: isLoading,
      teachersError: error,
      teachersValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetAllUsers(tempToken = null) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    zerreactorBackendEndpoints.User.GetAll,
    { ...authenticatedSwrOptions, tempToken }
  );

  return useMemo(
    () => ({
      users: data?.Response || data || [],
      usersLoading: isLoading,
      usersError: error,
      usersValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetUserById(userId) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    zerreactorBackendEndpoints.User.GetById(userId),
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      user: data || {},
      userLoading: isLoading,
      userError: error,
      userValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}
